import React from 'react';
import { Building, DollarSign } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

export const LateCheckoutReceipt = ({
  request,
  feeAmount,
  hoursLate,
  gracePeriodMinutes,
  hourlyRate,
  receiptNumber,
  reason,
  organizationInvoiceData = { orgNameInvoice: "EGARHUB", footerInvoice: "" },
}) => {
  const { t } = useTranslation();

  const currentDate = moment().format('L');
  const currentTime = moment().format('LT');

  return (
    <div className="bg-white p-8 max-w-md mx-auto font-mono text-sm">
      {/* Header */}
      <div className="text-center mb-6 border-b-2 border-dashed border-gray-300 pb-4">
        <div className="flex justify-center mb-2">
          <Building size={32} className="text-gray-600" />
        </div>
        <h1 className="text-lg font-bold text-gray-900">
          {organizationInvoiceData?.orgNameInvoice || organizationInvoiceData?.orgName}
        </h1>
        <p className="text-xs text-gray-500 mt-1">
          {t('lateCheckoutReceipt')}
        </p>
      </div>

      {/* Receipt Info */}
      <div className="mb-4 space-y-1">
        <div className="flex justify-between">
          <span className="text-gray-600">{t('receiptNumber')}</span>
          <span className="font-semibold">{receiptNumber}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('date')}</span>
          <span className="">{currentDate}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('time')}</span>
          <span className="">{currentTime}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('requestNumber')}</span>
          <span className="">{request.reqCode}</span>
        </div>
      </div>

      <div className="border-t border-dashed border-gray-300 pt-3 mb-4"></div>

      {/* Guest Info */}
      <div className="mb-4 space-y-1">
        <h3 className="font-semibold text-gray-800 mb-2">
          {t('guestInformation')}
        </h3>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('name')}</span>
          <span className="font-medium">{request.reqClientName}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('phone')}</span>
          <span dir="ltr">{request.client.clientPhone}</span>
        </div>
        {/* <div className="flex justify-between">
          <span className="text-gray-600">{t('email')}</span>
          <span className="text-xs">{request.email}</span>
        </div> */}
      </div>

      <div className="border-t border-dashed border-gray-300 pt-3 mb-4"></div>

      {/* Booking Info */}
      <div className="mb-4 space-y-1">
        <h3 className="font-semibold text-gray-800 mb-2">
          {t('bookingDetails')}
        </h3>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('unit')}</span>
          <span>{request.unit.unitCode} - {request.unit.unitName}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('checkIn')}</span>
          <span>{moment(request.reqFrom).format('L')}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('originalCheckout')}</span>
          <span>{moment(request.reqTo).format('L')} {moment(request.reqTo).format('LT a')}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('actualCheckout')}</span>
          <span>{moment().format('L')} {moment(request.reqTo).format('LT a')}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('guests')}</span>
          <span>{request.reqGuests}</span>
        </div>
      </div>

      <div className="border-t border-dashed border-gray-300 pt-3 mb-4"></div>

      {/* Late Checkout Fee Info */}
      <div className="mb-6 space-y-1">
        <h3 className="font-semibold text-gray-800 mb-2">
          {t('lateCheckoutFeeDetails')}
        </h3>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('lateDuration')}</span>
          <span className="font-medium">{hoursLate.toFixed(1)} {hoursLate === 1 ? t('hour') : t('hours')}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('hourlyRate')}</span>
          <span>{hourlyRate} {t('EGP')}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('gracePeriod')}</span>
          <span>{gracePeriodMinutes || 30} {t('minutes')} ({t('free')})</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('feeAmount')}</span>
          <span className="font-bold text-lg text-orange-600">{feeAmount.toLocaleString()} {t('EGP')}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('paymentMethod')}</span>
          <span>{t('cash')}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('collectionDate')}</span>
          <span>{currentDate}</span>
        </div>
        {reason && (
          <div className="mt-2">
            <span className="text-gray-600">{t('reason')}</span>
            <p className="text-xs mt-1 text-gray-700">{reason}</p>
          </div>
        )}
      </div>

      <div className="border-t-2 border-dashed border-gray-300 pt-4 mb-6"></div>

      {/* Fee Summary */}
      <div className="mb-6 bg-orange-50 p-3 rounded">
        <h4 className="font-semibold text-orange-800 mb-2">
          {t('feeSummary')}
        </h4>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">{t('originalBookingAmount')}</span>
            <span>{(request.totalAmount || request.reqTotalCost || 0).toLocaleString()} {t('EGP')}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">{t('lateCheckoutFee')}</span>
            <span className="font-bold text-orange-600">+{feeAmount.toLocaleString()} {t('EGP')}</span>
          </div>
          <div className="flex justify-between border-t border-orange-200 pt-1">
            <span className="font-semibold text-orange-700">{t('totalAmountDue')}</span>
            <span className="font-bold text-orange-700">{(((request.totalAmount || request.reqTotalCost || 0) + feeAmount)).toLocaleString()} {t('EGP')}</span>
          </div>
        </div>
      </div>

      {/* Payment Confirmation */}
      <div className="mb-6 bg-green-50 border border-green-200 p-3 rounded">
        <div className="flex items-start space-x-2 space-x-reverse">
          <DollarSign size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs text-green-800 font-semibold mb-1">
              {t('paymentConfirmation')}
            </p>
            <p className="text-xs text-green-700">
              {t('paymentAcknowledgment')}
            </p>
          </div>
        </div>
      </div>

      {/* Signatures */}
      <div className="space-y-6 mb-6">
        <div>
          <p className="text-xs text-gray-600 mb-3">
            {t('guestSignaturePayment')}
          </p>
          <div className="border-b-2 border-gray-400 h-10 mb-2"></div>
          <div className="flex justify-between text-xs text-gray-500">
            <span>{t('signature')}</span>
            <span>{t('date')}</span>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {t('paymentAcknowledgmentText')}
          </p>
        </div>

        <div>
          <p className="text-xs text-gray-600 mb-3">
            {t('authorizedStaffSignature')}
          </p>
          <div className="border-b-2 border-gray-400 h-10 mb-2"></div>
          <div className="flex justify-between text-xs text-gray-500">
            <span>{t('signatureAndStamp')}</span>
            <span>{t('date')}</span>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {t('authorizedFrontOfficeStaff')}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center text-xs text-gray-500 border-t border-dashed border-gray-300 pt-3">
        <p>{t('thankYou')}</p>
        <p className="mt-1">
          {t('inquiries')} <span dir="ltr">{organizationInvoiceData?.orgPhone || 'info@egarhub.com'}</span>
        </p>
        <p className="mt-2 text-xs font-semibold">
          {t('keepForRecords')}
        </p>
        <p className="mt-2 text-xs">
          {organizationInvoiceData?.footerInvoice}
        </p>
      </div>
    </div>
  );
};